// src/encounter/structured-note/label-parts.tsx
//
// Section 4 · the visit note labels (Antonin's catalog, scrips-signal-ds @f28d00c1, 2 Nov), behind `encounterLatest`
// (`?x=encounter-latest`). Display only: every word of the label stays; the answer, its write and the aria labels are unchanged.
//   · a qualifier (a trailing "(…)" or what follows " — ") is drawn one level down, `[data-label-qualifier]`;
//   · a label that only repeats the title of its group is kept for screen readers only, `[data-label-repeats-title]`.
// The look is `src/styles/showcase-encounter-latest.css`; the model is `label-parts-model.ts`. Switch off: the plain text, as before.
import { useContext, type ReactNode } from 'react';
import { Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@scripsteam/scrips-signal-ds';
import { NoteGroupTitleContext, sameLabel, shortLabel, splitLabel, stateTwinLabel, useEncounterLatest, useVisitNoteV2 } from './label-parts-model';

/** A note line's label: a string label gets its qualifier one level down, or is kept for screen readers only when it
 *  repeats its group's title. Any other label (a node) passes through. */
export function NoteLabel({ label, stateTwin }: { label: ReactNode; stateTwin?: boolean }) {
  const on = useEncounterLatest();
  const noteV2 = useVisitNoteV2();
  const groupTitle = useContext(NoteGroupTitleContext);
  if (noteV2 && typeof label === 'string') {
    // A "-state" twin under its base item reads as that item's reason line, not as a second copy of its label.
    const [shown, full] = stateTwin ? stateTwinLabel(label) : shortLabel(label);
    if (full) return <ShortLabel shown={on ? <LatestLabel label={shown} groupTitle={groupTitle} /> : shown} name={shown} full={full} />;
  }
  if (!on || typeof label !== 'string') return <>{label}</>;
  return <LatestLabel label={label} groupTitle={groupTitle} />;
}

/** The Section 4 label parts of a string label (qualifier one level down, a repeated group title for screen readers only). */
function LatestLabel({ label, groupTitle }: { label: string; groupTitle: string }) {
  if (groupTitle && sameLabel(groupTitle, label)) return <span data-label-repeats-title="">{label}</span>;
  const [main, qual] = splitLabel(label);
  return qual ? <>{main}<span data-label-qualifier=""> {qual}</span></> : <>{label}</>;
}

/** List A (PROD-3395): the short label, then an info icon whose tooltip holds the label's full text (hover and keyboard focus). */
function ShortLabel({ shown, name, full }: { shown: ReactNode; name: string; full: string }) {
  return (
    <span className="inline-flex items-center gap-1" data-short-label="">
      <span>{shown}</span>
      <Tooltip>
        <TooltipTrigger
          render={<button type="button" aria-label={`About ${name}`} data-label-info="" className="inline-flex size-6 items-center justify-center rounded-sm text-text-secondary hover:text-text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary" />}
        >
          <Info className="size-4" aria-hidden />
        </TooltipTrigger>
        <TooltipContent className="max-w-80">{full}</TooltipContent>
      </Tooltip>
    </span>
  );
}
